import * as React from "react";
import { Link } from "react-router-dom";
import { AtSign, Trash2, RefreshCw, ChevronDown, Copy, ExternalLink } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from "@/components/ui/alert-dialog";
import { AddMailboxDialog } from "./AddMailboxDialog";
import { useMailboxes, useDeleteMailbox } from "@/hooks/useMailboxes";
import { useToast } from "@/hooks/use-toast";
import { ApiError } from "@/lib/api";
import type { MailboxDto } from "@/types/api";

interface MailboxListProps {
  containerId: number;
  domainName: string;
  hasPackage: boolean;
  /** Mailboxes included in the plan pool. -1 = unlimited. */
  allocated?: number;
}

export function MailboxList({ containerId, domainName, hasPackage, allocated = 0 }: MailboxListProps) {
  const { data, isLoading, error, refetch, isFetching } = useMailboxes(
    hasPackage ? containerId : undefined
  );

  const used = data?.length ?? 0;
  const unlimited = allocated < 0;
  const atLimit = hasPackage && !unlimited && allocated > 0 && used >= allocated;

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="font-display text-lg font-semibold">Mailboxes</h2>
          <p className="text-sm text-muted-foreground">
            {hasPackage ? (
              <>
                <span className="font-medium text-foreground">
                  {used} of {unlimited ? "∞" : allocated}
                </span>{" "}
                mailbox{allocated === 1 ? "" : "es"} used on your plan for {domainName}.
              </>
            ) : (
              <>Email hosting for {domainName}.</>
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {hasPackage && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => void refetch()}
              disabled={isFetching}
            >
              <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          )}
          <AddMailboxDialog
            containerId={containerId}
            domainName={domainName}
            atLimit={atLimit}
          />
        </div>
      </div>

      {atLimit && (
        <p className="rounded-lg border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
          {domainName} has used all {allocated} of its mailboxes.{" "}
          <Link to="/add-ons" className="font-medium text-primary hover:underline">
            Add more mailboxes to this domain
          </Link>{" "}
          — or delete one you no longer need.
        </p>
      )}

      {!hasPackage ? (
        <NotProvisionedState domainName={domainName} />
      ) : isLoading ? (
        <ListSkeleton />
      ) : error ? (
        <ErrorState error={error} />
      ) : !data || data.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {data.map(m => (
            <MailboxCard key={m.id} mailbox={m} containerId={containerId} />
          ))}
        </div>
      )}
    </div>
  );
}

function MailboxCard({ mailbox, containerId }: { mailbox: MailboxDto; containerId: number }) {
  const { toast } = useToast();
  const del = useDeleteMailbox(containerId);
  const [open, setOpen] = React.useState(false);

  async function onDelete() {
    try {
      await del.mutateAsync(mailbox.id);
      toast({
        title: "Mailbox deleted",
        description: `${mailbox.email} is gone.`,
        variant: "success"
      });
    } catch (err) {
      toast({
        title: "Couldn't delete",
        description: err instanceof ApiError ? err.message : "Network error.",
        variant: "destructive"
      });
    }
  }

  async function copy(label: string, value: string) {
    try {
      await navigator.clipboard.writeText(value);
      toast({ title: `${label} copied`, variant: "success" });
    } catch {
      toast({ title: "Couldn't copy", variant: "destructive" });
    }
  }

  const usagePct = mailbox.quotaMb > 0
    ? Math.min(100, Math.round((mailbox.usedMb / mailbox.quotaMb) * 100))
    : 0;

  const domain = mailbox.email.split("@")[1] ?? "";
  const mailHost = `mail.${domain}`;
  const webmail = `https://${mailHost}`;
  const isActive = (mailbox.status || "active").toLowerCase() === "active";

  return (
    <Card className="overflow-hidden transition-shadow hover:shadow-brand-sm">
      <CardContent className="flex flex-col gap-3 p-4">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-accentSoft text-primary">
            <AtSign className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-foreground">{mailbox.email}</p>
            <p className="truncate text-xs text-muted-foreground">
              {mailbox.displayName || "Mailbox"} ·{" "}
              <span className={isActive ? "text-brand-green" : "text-amber-600"}>
                {isActive ? "Active" : mailbox.status}
              </span>
            </p>
          </div>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button
                className="rounded-md p-2 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                aria-label={`Delete ${mailbox.email}`}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this mailbox?</AlertDialogTitle>
                <AlertDialogDescription>
                  {mailbox.email} and all its messages will be permanently removed from 20i.
                  This can't be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Keep mailbox</AlertDialogCancel>
                <AlertDialogAction
                  onClick={onDelete}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Delete mailbox
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>

        <div>
          <div className="flex items-baseline justify-between text-xs text-muted-foreground">
            <span>{Math.round(mailbox.usedMb)} MB used</span>
            <span>{Math.round(mailbox.quotaMb)} MB quota</span>
          </div>
          <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted">
            <div className="h-full bg-primary" style={{ width: `${usagePct}%` }} />
          </div>
        </div>

        {/* Expand to connection details */}
        <button
          type="button"
          onClick={() => setOpen(v => !v)}
          aria-expanded={open}
          className="inline-flex items-center justify-center gap-1.5 rounded-md border border-border bg-muted/40 px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:border-primary/40 hover:bg-brand-accent/5"
        >
          {open ? "Hide details" : "View details"}
          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>

        {open && (
          <div className="space-y-2 rounded-lg border border-border bg-muted/30 p-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Connect this mailbox
            </p>
            <DetailRow label="Username" value={mailbox.email} onCopy={() => copy("Username", mailbox.email)} />
            <DetailRow label="Incoming (IMAP)" value={`${mailHost} · 993 · SSL`} onCopy={() => copy("IMAP host", mailHost)} />
            <DetailRow label="Outgoing (SMTP)" value={`${mailHost} · 465 · SSL`} onCopy={() => copy("SMTP host", mailHost)} />
            <DetailRow label="Password" value="The one you set when creating it" />
            <a
              href={webmail}
              target="_blank"
              rel="noreferrer"
              className="mt-1 inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline"
            >
              Open webmail <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function DetailRow({ label, value, onCopy }: { label: string; value: string; onCopy?: () => void }) {
  return (
    <div className="flex items-center justify-between gap-2 text-xs">
      <span className="shrink-0 text-muted-foreground">{label}</span>
      <span className="flex min-w-0 items-center gap-1.5">
        <span className="truncate font-medium text-foreground">{value}</span>
        {onCopy && (
          <button
            type="button"
            onClick={onCopy}
            className="shrink-0 rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            aria-label={`Copy ${label}`}
          >
            <Copy className="h-3 w-3" />
          </button>
        )}
      </span>
    </div>
  );
}

// ── States ────────────────────────────────────────────────────
function NotProvisionedState({ domainName }: { domainName: string }) {
  return (
    <Card>
      <CardContent className="py-8 text-center text-sm text-muted-foreground">
        Email hosting for {domainName} is still being set up. It activates
        automatically a few minutes after checkout — check back shortly.
      </CardContent>
    </Card>
  );
}

function ListSkeleton() {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {Array.from({ length: 4 }).map((_, i) => (
        <Card key={i}>
          <CardContent className="space-y-3 p-4">
            <div className="flex items-center gap-3">
              <Skeleton className="h-9 w-9 rounded-lg" />
              <div className="flex-1 space-y-1">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-20" />
              </div>
            </div>
            <Skeleton className="h-2 w-full" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function ErrorState({ error }: { error: unknown }) {
  const message =
    error instanceof ApiError ? `${error.message} (${error.code ?? error.status})` : "Network error.";
  return (
    <Card>
      <CardContent className="py-8 text-center text-sm text-destructive">
        Couldn't load mailboxes: {message}
      </CardContent>
    </Card>
  );
}

function EmptyState() {
  return (
    <Card>
      <CardContent className="py-8 text-center text-sm text-muted-foreground">
        No mailboxes yet. Create one to get started.
      </CardContent>
    </Card>
  );
}
